import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './client';
import type {
  ApprovalChain,
  ApprovalRequest,
  ApprovalActionType,
  ApprovalRequestStatus,
} from '@/types/approvals';

// ── Filter / Payload Types ─────────────────────────────────────────────────

export interface ApprovalChainFilter {
  action_type?: ApprovalActionType;
}

export interface ApprovalChainCreatePayload {
  name: string;
  action_type: ApprovalActionType;
  description?: string;
  levels: {
    level_order: number;
    approver_type: 'role' | 'specific_user';
    approver_role?: string;
    approver_user?: string;
    escalation_timeout_hours: number;
    auto_approve_rules?: Record<string, unknown>;
  }[];
}

export interface ApprovalChainUpdatePayload {
  id: string;
  name?: string;
  description?: string;
  levels?: ApprovalChainCreatePayload['levels'];
}

export interface ApprovalRequestFilter {
  status?: ApprovalRequestStatus;
  initiated_by?: string;
  action_type?: ApprovalActionType;
  cursor?: string;
  page_size?: number;
}

export interface PaginatedApprovalRequests {
  results: ApprovalRequest[];
  next_cursor: string | null;
  previous_cursor: string | null;
  page_size: number;
}

// ── Query Keys ─────────────────────────────────────────────────────────────

export const approvalKeys = {
  all: ['approvals'] as const,
  chains: () => [...approvalKeys.all, 'chains'] as const,
  chainList: (filters: ApprovalChainFilter) => [...approvalKeys.chains(), 'list', filters] as const,
  chainDetail: (id: string) => [...approvalKeys.chains(), 'detail', id] as const,
  requests: () => [...approvalKeys.all, 'requests'] as const,
  requestList: (filters: ApprovalRequestFilter) => [...approvalKeys.requests(), 'list', filters] as const,
  pending: () => [...approvalKeys.all, 'pending'] as const,
};

// ── Approval Chain Hooks ───────────────────────────────────────────────────

export function useApprovalChains(filters: ApprovalChainFilter = {}) {
  return useQuery({
    queryKey: approvalKeys.chainList(filters),
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (filters.action_type) params.action_type = filters.action_type;

      const { data } = await apiClient.get<ApprovalChain[]>('/approvals/chains/', { params });
      return data;
    },
  });
}

export function useApprovalChain(id: string) {
  return useQuery({
    queryKey: approvalKeys.chainDetail(id),
    queryFn: async () => {
      const { data } = await apiClient.get<ApprovalChain>(`/approvals/chains/${id}/`);
      return data;
    },
    enabled: !!id,
  });
}

export function useCreateApprovalChain() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ApprovalChainCreatePayload) => {
      const { data } = await apiClient.post<ApprovalChain>('/approvals/chains/', payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: approvalKeys.chains() });
    },
  });
}

export function useUpdateApprovalChain() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: ApprovalChainUpdatePayload) => {
      const { data } = await apiClient.patch<ApprovalChain>(`/approvals/chains/${id}/`, payload);
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: approvalKeys.chains() });
      queryClient.invalidateQueries({ queryKey: approvalKeys.chainDetail(variables.id) });
    },
  });
}

export function useActivateChain() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post<ApprovalChain>(`/approvals/chains/${id}/activate/`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: approvalKeys.chains() });
    },
  });
}

export function useDeactivateChain() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post<ApprovalChain>(`/approvals/chains/${id}/deactivate/`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: approvalKeys.chains() });
    },
  });
}

// ── Approval Request Hooks ─────────────────────────────────────────────────

export function useApprovalRequest(id: string) {
  return useQuery({
    queryKey: [...approvalKeys.requests(), 'detail', id] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<ApprovalRequest>(`/approvals/requests/${id}/`);
      return data;
    },
    enabled: !!id,
  });
}

export function useApprovalRequests(filters: ApprovalRequestFilter = {}) {
  return useQuery({
    queryKey: approvalKeys.requestList(filters),
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (filters.status) params.status = filters.status;
      if (filters.initiated_by) params.initiated_by = filters.initiated_by;
      if (filters.action_type) params.action_type = filters.action_type;
      if (filters.cursor) params.cursor = filters.cursor;
      if (filters.page_size) params.page_size = String(filters.page_size);

      const { data } = await apiClient.get<PaginatedApprovalRequests>('/approvals/requests/', { params });
      return data;
    },
  });
}

export function usePendingApprovals() {
  return useQuery({
    queryKey: approvalKeys.pending(),
    queryFn: async () => {
      const { data } = await apiClient.get<ApprovalRequest[]>('/approvals/requests/pending/');
      return data;
    },
  });
}

export function useApproveRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, comment }: { id: string; comment?: string }) => {
      const payload: Record<string, string> = {};
      if (comment) payload.comment = comment;
      const { data } = await apiClient.post<ApprovalRequest>(`/approvals/requests/${id}/approve/`, payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: approvalKeys.requests() });
      queryClient.invalidateQueries({ queryKey: approvalKeys.pending() });
    },
  });
}

export function useRejectRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, comment }: { id: string; comment: string }) => {
      const { data } = await apiClient.post<ApprovalRequest>(`/approvals/requests/${id}/reject/`, { comment });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: approvalKeys.requests() });
      queryClient.invalidateQueries({ queryKey: approvalKeys.pending() });
    },
  });
}
